import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { useApi } from "@/lib/fetch-client";
import { getErrorMessage } from "@/lib/utils";

export interface Chat {
	id: string;
	title: string;
	model: string;
	status: "active" | "archived" | "deleted";
	webSearch: boolean;
	createdAt: string;
	updatedAt: string;
	messageCount: number;
}

export interface ChatMessage {
	id: string;
	role: "user" | "assistant" | "system";
	content: string | null;
	images: string | null; // JSON string from API
	reasoning: string | null; // Reasoning content from AI
	sequence: number;
	createdAt: string;
}

export function useChats() {
	const api = useApi();

	return api.useQuery("get", "/chats");
}

export function useDataChat(chatId: string) {
	const api = useApi();

	return api.useQuery(
		"get",
		"/chats/{id}",
		{
			params: {
				path: { id: chatId },
			},
		},
		{
			enabled: !!chatId,
		},
	);
}

export function useCreateChat() {
	const queryClient = useQueryClient();
	const api = useApi();

	return api.useMutation("post", "/chats", {
		onSuccess: () => {
			const queryKey = api.queryOptions("get", "/chats").queryKey;
			void queryClient.invalidateQueries({ queryKey });
			toast("Chat created successfully");
		},
		onError: (error) => {
			toast.error(getErrorMessage(error));
		},
	});
}

export function useUpdateChat() {
	const queryClient = useQueryClient();
	const api = useApi();

	return api.useMutation("patch", "/chats/{id}", {
		onSuccess: () => {
			const queryKey = api.queryOptions("get", "/chats").queryKey;
			void queryClient.invalidateQueries({ queryKey });
			toast("Chat updated successfully");
		},
		onError: (error) => {
			toast.error(getErrorMessage(error));
		},
	});
}

export function useDeleteChat() {
	const queryClient = useQueryClient();
	const api = useApi();

	return api.useMutation("delete", "/chats/{id}", {
		onSuccess: () => {
			const queryKey = api.queryOptions("get", "/chats").queryKey;
			void queryClient.invalidateQueries({ queryKey });
			toast("Chat deleted successfully");
		},
		onError: (error) => {
			toast.error(getErrorMessage(error));
		},
	});
}

export function useAddMessage() {
	const queryClient = useQueryClient();
	const api = useApi();

	return api.useMutation("post", "/chats/{id}/messages", {
		onSuccess: (_data, variables) => {
			// Invalidate the chats list
			const chatsQueryKey = api.queryOptions("get", "/chats").queryKey;
			void queryClient.invalidateQueries({ queryKey: chatsQueryKey });

			// Also invalidate the specific chat query to ensure fresh data when switching back
			const chatId = variables.params?.path?.id;
			if (chatId) {
				const chatQueryKey = api.queryOptions("get", "/chats/{id}", {
					params: { path: { id: chatId } },
				}).queryKey;
				void queryClient.invalidateQueries({ queryKey: chatQueryKey });
			}
		},
		onError: (error) => {
			toast.error(getErrorMessage(error));
		},
	});
}
